import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import React, { useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, { FadeIn, FadeInDown, useReducedMotion } from 'react-native-reanimated';

import { AppText } from '@/components/ui/AppText';
import { Button } from '@/components/ui/Button';
import { useThemePalette } from '@/config/theme';
import { markIntroSeen } from '@/lib/intro';

const brandMark = require('../../assets/images/brand-mark.png');

type IoniconName = keyof typeof Ionicons.glyphMap;

const FEATURES: { icon: IoniconName; key: string }[] = [
  { icon: 'football', key: 'live' },
  { icon: 'shirt', key: 'fantasy' },
  { icon: 'game-controller', key: 'games' },
  { icon: 'swap-horizontal', key: 'transfers' },
];

function FeatureRow({ icon, title, index }: { icon: IoniconName; title: string; index: number }) {
  const palette = useThemePalette();
  const reduce = useReducedMotion();
  return (
    <Animated.View
      entering={reduce ? undefined : FadeInDown.delay(260 + index * 70).springify()}
      className="flex-row items-center gap-3"
    >
      <View className="h-9 w-9 items-center justify-center rounded-xl bg-volt-glass">
        <Ionicons name={icon} size={18} color={palette.voltBright} />
      </View>
      <AppText variant="caption" weight="semibold" className="flex-1 text-soft" numberOfLines={1}>
        {title}
      </AppText>
    </Animated.View>
  );
}

/**
 * First-launch intro / Quick Setup. Branded neon hero + the app's headline
 * promise, then two doors: start (sign in / Quick Setup) or explore as a guest.
 * Shown once - the (tabs) gate sends first-time, signed-out users here and both
 * CTAs persist the "seen" flag so it never reappears.
 */
export default function IntroScreen() {
  const palette = useThemePalette();
  const router = useRouter();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const reduce = useReducedMotion();
  const [busy, setBusy] = useState<'start' | 'explore' | null>(null);

  const go = async (target: 'start' | 'explore') => {
    if (busy) return;
    setBusy(target);
    await markIntroSeen();
    // "Get Started" -> the guided picker (pick teams/nations + players FIRST,
    // then sign in). "Explore" -> straight into the app as a guest.
    router.replace(target === 'start' ? '/auth/welcome' : '/(tabs)');
  };

  return (
    <View className="flex-1" style={{ backgroundColor: palette.gradBottom }}>
      <LinearGradient
        colors={[palette.gradTop, palette.gradMid, palette.gradBottom]}
        locations={[0, 0.55, 1]}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
      />
      <View
        className="flex-1 px-6"
        style={{ paddingTop: insets.top + 48, paddingBottom: insets.bottom + 20 }}
      >
        {/* Hero */}
        <View className="flex-1 items-center justify-center">
          <Animated.View
            entering={reduce ? undefined : FadeIn.duration(600)}
            className="items-center"
          >
            <View className="items-center justify-center">
              <View
                className="absolute h-44 w-44 rounded-full"
                style={{ backgroundColor: palette.brandCyan, opacity: 0.12 }}
              />
              <LinearGradient
                colors={[palette.brandMagenta, palette.brandViolet, palette.brandCyan]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{ width: 124, height: 124, borderRadius: 38, alignItems: 'center', justifyContent: 'center' }}
              >
                <View
                  className="items-center justify-center overflow-hidden"
                  style={{ width: 117, height: 117, borderRadius: 34, backgroundColor: palette.gradBottom }}
                >
                  <Image source={brandMark} style={{ width: 100, height: 100, borderRadius: 28 }} contentFit="contain" />
                </View>
              </LinearGradient>
            </View>
          </Animated.View>

          <Animated.View
            entering={reduce ? undefined : FadeInDown.delay(140).springify()}
            className="mt-9 items-center"
          >
            <AppText variant="display" weight="bold" className="text-center text-ink">
              {t('intro.headline')}
            </AppText>
            <AppText variant="body" className="mt-3 text-center text-soft">
              {t('intro.subtitle')}
            </AppText>
          </Animated.View>

          {/* Feature highlights */}
          <View className="mt-9 w-full gap-3.5 px-2">
            {FEATURES.map((feature, index) => (
              <FeatureRow
                key={feature.key}
                icon={feature.icon}
                title={t(`intro.feature.${feature.key}`)}
                index={index}
              />
            ))}
          </View>
        </View>

        {/* CTAs */}
        <Animated.View entering={reduce ? undefined : FadeInDown.delay(560).springify()} className="gap-3">
          <Button
            label={t('intro.getStarted')}
            loading={busy === 'start'}
            disabled={busy !== null}
            onPress={() => go('start')}
          />
          <Button
            label={t('intro.explore')}
            variant="ghost"
            loading={busy === 'explore'}
            disabled={busy !== null}
            onPress={() => go('explore')}
          />
        </Animated.View>
      </View>
    </View>
  );
}
